import React, { useState, useCallback, useEffect } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { pickBy, head } from 'lodash';

import { aclHelper, usePersistState } from 'helper';

import { useCreateModal } from 'partial/components/Modal';
import AsyncButton from 'modules/common/components/AsyncButton';
import List from 'partial/components/List';
import SearchBar from 'partial/components/input/SearchBar';
import Pagination from 'partial/components/Pagination';
import ThumbIcon from 'partial/components/ThumbIcon';
import { useModuleContext } from 'partial/wrappers/ModuleWrapper';
import RoleDetails from './container/RoleDetails';
import AddUpdateRole from './modals/AddUpdateRole';
import { useRoles } from './hooks';

const INITIAL_STATE = {
  q: '',
  status: { label: '', value: '' },
};

const RolesAndPermissions = () => {
  const { setSubLabel } = useModuleContext();

  const navigate = useNavigate();
  const createModal = useCreateModal();
  const [filter, setFilter] = useState(INITIAL_STATE);
  const [selected, setSelected] = usePersistState('role-id', '', {
    keepOnUnmount: false,
  });

  const [isLoading, roles, meta] = useRoles(
    pickBy({ ...filter, status: filter?.status?.value }),
  );

  const id = head(roles)?.id;

  const onSubSelect = (item) => {
    setSelected(item?.id);
    navigate(`${item?.id}`);
  };

  const handleAddRole = useCallback(() => {
    createModal({
      content: (onClose) => <AddUpdateRole onClose={onClose} />,
    });
  }, [createModal]);

  useEffect(() => {
    if (!id) return;
    setSelected(id);
  }, [id, setSelected]);

  useEffect(() => {
    if (!meta) return;
    setSubLabel(
      meta?.total === 0
        ? ''
        : `Total of ${meta?.total} role${meta?.total === 1 ? '' : 's'}`,
    );
    return () => setSubLabel('');
  }, [meta, setSubLabel]);

  return (
    <section className="flex p-6 bg-white drop-shadow rounded-lg flex-grow">
      <div className="flex flex-col gap-6 pr-4 border-r border-gray-50 w-[260px]">
        <SearchBar
          name="q"
          value={filter?.q}
          onChange={setFilter}
          inputClassName="py-[1.05rem] !w-full"
        />

        <List
          data={roles}
          isLoading={isLoading}
          selectedId={selected}
          selectedKey="id"
          onSelectRow={onSubSelect}
          containerClassName="!w-full"
          render={(item) => {
            const name = item?.name;
            return (
              <div className="flex items-center gap-2 pl-2 py-1 w-full">
                <ThumbIcon className="h-8 w-8 rounded-full" alt={name} />
                <div className="flex flex-col text-sm">
                  <span className="capitalize">{name}</span>
                  <span className="lowercase">{item?.code}</span>
                </div>
              </div>
            );
          }}
        />
        <div className="flex justify-center">
          <Pagination onChange={setFilter} value={meta} withPage={false} />
        </div>
        {aclHelper(
          'role',
          'w',
          <AsyncButton className="w-full" onClick={handleAddRole}>
            add roles
          </AsyncButton>,
        )}
      </div>

      <Routes>
        <Route path=":id/*" element={<RoleDetails roles={roles} />} />
        {roles?.length > 0 && (
          <Route
            index
            element={<Navigate replace to={`${head(roles)?.id}`} />}
          />
        )}
      </Routes>
    </section>
  );
};

export default RolesAndPermissions;
